/**
 * Run a declared action (spec section 2): the person sees the label, what it does, the exact
 * command, the folder and the machine, then the label itself (already verb and object) or "Cancel". The same dialog then
 * follows the run live: waiting for a landing (with Start now), running, succeeded, or failed
 * with its exit code and its log.
 */
import { Hourglass, ScrollText } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Dialog, ErrorState, LogViewer, RelativeTime, RowStatus, formatDuration, shortPath, toUiError, useNow } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { cancelRun, confirmRun, runDeclaredAction, usePpSlice } from './api.ts';
import { ProtocolLog } from './RunLog.tsx';
import { HoldNote } from './Holds.tsx';
import { actionFolder, busyOn, busyText, declaredAction, holdById, isRunLive, mainCheckout, reportFor, runStatus } from './model.ts';
import { CeilingHint } from '../trust/CeilingHint.tsx';
import { ceilingCommand } from '../trust/ceilingHint.ts';
import { closePpDialogs, usePpDialogs } from './store.ts';


function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

export function Mono({ children }: { children: string }) {
  return <code className="block select-all break-all rounded-control border border-border bg-bg px-3 py-2 font-mono text-caption text-fg">{children}</code>;
}

export function ActionDialogHost() {
  const target = usePpDialogs((s) => s.action);
  const state = usePpSlice();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const checkouts = useOrgus((s) => s.checkouts);
  const now = useNow(1000);
  const [runId, setRunId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    setRunId(target?.runId ?? null);
    setShowLog(false);
    setError(null);
    setBusy(false);
  }, [target]);

  const report = target ? reportFor(state, target.projectId, target.machineId) : undefined;
  const decl = target ? declaredAction(report, target.actionId) : undefined;
  const machine = target ? machines[target.machineId] : undefined;
  const main = useMemo(() => (target ? mainCheckout(Object.values(checkouts), target.projectId, target.machineId) : undefined), [checkouts, target]);
  const run = runId ? state?.runs.find((r) => r.id === runId) : undefined;
  const hold = holdById(state, run?.yieldId);
  // A heavy action waits while another project on the machine lands or deploys.
  const blocking = decl?.heavy && target ? busyOn(state, target.machineId).find((b) => b.projectId !== target.projectId) : undefined;
  const projectName = (id: string) => projects[id]?.name;

  const close = () => closePpDialogs();
  const start = async () => {
    if (!target || !decl) return;
    setBusy(true);
    setError(null);
    try {
      const r = await runDeclaredAction({ ...target, shownCommand: decl.run });
      setRunId(r.id);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      await confirmRun(run.id);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (thenClose = false) => {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      await cancelRun(run.id);
      if (thenClose) close();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const open = !!target;
  if (!open) return null;

  // A run opened from a list stays readable after its action left orgus.yml.
  const label = decl?.label ?? run?.actionId ?? target.actionId;
  if (!decl && !run) {
    return (
      <Dialog open onOpenChange={(v) => !v && close()} title="Action not available" size="sm" footer={<Button onClick={close}>Close</Button>}>
        <ErrorState
          variant="inline"
          error={{ what: 'This action is not declared.', why: `${projects[target.projectId]?.name ?? 'The project'} does not list it in orgus.yml at HEAD on ${machine?.name ?? 'that machine'}.`, next: 'Check the project, then try again.' }}
        />
      </Dialog>
    );
  }

  const command = run?.command ?? decl?.run ?? '';
  const folder = actionFolder(main?.path, run?.cwd ?? decl?.cwd ?? '.');
  const facts = (
    <dl className="flex flex-col gap-3">
      <Fact label="Command">
        <Mono>{command}</Mono>
      </Fact>
      <div className="grid gap-3 sm:grid-cols-2">
        <Fact label="Folder">
          <span className="block truncate font-mono text-caption text-fg-muted" title={folder}>
            {shortPath(folder, 40)}
          </span>
        </Fact>
        <Fact label="Machine">
          <span className="text-ui text-fg-muted">{machine?.name ?? 'Unknown machine'}</span>
        </Fact>
      </div>
    </dl>
  );

  if (run) {
    const st = runStatus(run, hold);
    const live = isRunLive(run);
    const waiting = st.label === 'Waiting';
    // Requested elsewhere (the board's button on another tab, the CLI): it runs only once a person confirms here.
    const awaiting = run.state === 'awaiting-confirmation';
    const ended = run.endedAt ?? (live ? now : run.startedAt ?? now);
    const demoLog = [`$ ${run.command}`, ...(run.state === 'failed' ? [run.reason ?? 'Failed', `Exited with code ${run.exitCode ?? 1}`] : ['Done', `Exited with code ${run.exitCode ?? 0}`])].join('\n');
    return (
      <Dialog
        open
        onOpenChange={(v) => !v && close()}
        title={label}
        description={decl?.does}
        size={showLog ? 'lg' : 'md'}
        footer={
          awaiting ? (
            <>
              <Button variant="secondary" disabled={busy} onClick={() => void cancel(true)}>
                Cancel
              </Button>
              <Button variant="primary" loading={busy} onClick={() => void confirm()}>
                {label}
              </Button>
            </>
          ) : (
          <>
            {waiting && (
              <Button variant="secondary" loading={busy} onClick={() => void cancel()}>
                Cancel run
              </Button>
            )}
            <Button variant={waiting ? 'ghost' : 'secondary'} onClick={close}>
              Close
            </Button>
          </>
          )
        }
      >
        <div className="flex flex-col gap-4" aria-live="polite">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
            <RowStatus {...st} />
            {run.startedAt != null && (
              <span className="text-caption text-fg-subtle">
                <RelativeTime ts={run.startedAt} prefix="Started" />
              </span>
            )}
            {run.startedAt != null && !waiting && <span className="text-caption text-fg-subtle num">{formatDuration(ended - run.startedAt)}</span>}
            {(run.logRef || run.tail) && !live && (
              <Button size="sm" variant="ghost" icon={<ScrollText />} className="ml-auto" aria-expanded={showLog} onClick={() => setShowLog((v) => !v)}>
                {showLog ? 'Hide log' : 'Show log'}
              </Button>
            )}
          </div>
          {hold && waiting && <HoldNote hold={hold} />}
          {(run.state === 'failed' || run.state === 'refused') && (
            <ErrorState
              variant="inline"
              error={{
                what: run.state === 'refused' ? 'The machine refused to run it.' : `${label} failed.`,
                why: run.reason ?? (run.exitCode != null ? `It exited with code ${run.exitCode}.` : 'The machine did not say why.'),
                next: run.logRef ? 'Show the log to see its output.' : 'Check the project, then run it again.',
              }}
            />
          )}
          {run.state === 'refused' && ceilingCommand(run.reason) && <CeilingHint command={ceilingCommand(run.reason)!} />}
          {error && <ErrorState variant="inline" error={error} />}
          {showLog && run.logRef ? (
            <ProtocolLog logRef={run.logRef} label={`Log for ${label}`} demo={run.tail ?? demoLog} />
          ) : showLog && run.tail ? (
            <LogViewer label={`Last output of ${label}`} text={run.tail} className="h-64" />
          ) : (
            facts
          )}
        </div>
      </Dialog>
    );
  }

  if (!decl) return null;
  return (
    <Dialog
      open
      onOpenChange={(v) => !busy && !v && close()}
      dismissible={!busy}
      title={decl.label}
      description={decl.does}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void start()}>
            {decl.label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {facts}
        {blocking && (
          <p className="flex items-center gap-2 text-caption text-fg-muted">
            <Hourglass className="size-3 shrink-0 text-fg-subtle" aria-hidden />
            Waits for {busyText(blocking, projectName)}, then starts by itself
          </p>
        )}
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
